<template>
  <!--
    Popover de INFO de un elemento eléctrico/zanjas (un click en el visor del
    LayoutBuilder en modo 'progress'). Componente "tonto": recibe los datos ya
    resueltos (extra_attributes, certificación por metros, progreso) y solo los
    presenta. El doble click abre el formulario de avance (otro flujo).
  -->
  <div class="epc-info-overlay">
    <!-- Resaltado del SEGMENTO + leader line (SVG a viewport completo, sin
         capturar clicks). Sigue al segmento en pan/zoom (el padre re-proyecta). -->
    <svg v-if="anchor" class="epc-info-svg">
      <polyline v-if="polylineStr" :points="polylineStr" class="epc-seg-halo" />
      <polyline v-if="polylineStr" :points="polylineStr" class="epc-seg-line" />
      <line v-if="leaderStart" :x1="leaderStart.x" :y1="leaderStart.y" :x2="anchor.x" :y2="anchor.y" class="epc-leader" />
      <circle :cx="anchor.x" :cy="anchor.y" r="4" class="epc-marker" />
    </svg>

  <div
    ref="box"
    class="epc-info-popover"
    :style="{ top: adjusted.y + 'px', left: adjusted.x + 'px' }"
  >
    <header class="epc-info-popover__header">
      <span class="epc-info-popover__title">{{ title }}</span>
      <button
        type="button"
        class="epc-info-popover__close"
        :aria-label="$t('close')"
        @click="$emit('close')"
      >
        <b-icon icon="times" pack="fas" size="is-small" />
      </button>
    </header>

    <div v-if="loading" class="epc-info-popover__loading">
      <b-icon icon="spinner" pack="fas" size="is-small" class="fa-spin mr-2" />
      <span>{{ $t('loading') }}…</span>
    </div>

    <div v-else class="epc-info-popover__body">
      <b-tabs type="is-boxed" size="is-small" v-model="activeTab" class="epc-info-popover__tabs" expanded>
        <!-- Campos extra asociados -->
        <b-tab-item>
          <template #header>
            <b-icon icon="list-ul" pack="fas" size="is-small" />
            <span class="ml-1">{{ $t('extra_attributes') }}</span>
          </template>
          <ul class="epc-kv">
            <li><span>{{ $t('order_line_id') }}</span><b>{{ extras.order_line_id || '—' }}</b></li>
            <li><span>{{ $t('order_id') }}</span><b>{{ extras.purchase_order_id || '—' }}</b></li>
            <li><span>{{ $t('zone') }}</span><b>{{ extras.zone_bqo || '—' }}</b></li>
          </ul>
        </b-tab-item>

        <!-- Certificación (cabecera total + desglose por certificación) -->
        <b-tab-item>
          <template #header>
            <b-icon icon="certificate" pack="fas" size="is-small" />
            <span class="ml-1">{{ $t('certification') }}</span>
          </template>
          <template v-if="certification">
            <div class="epc-cert-total">
              <b-icon icon="certificate" pack="fas" size="is-small" class="mr-1" />
              <span>{{ $t('total_certified') }}: <b>{{ fmt(certification.total_certified_meters) }} m</b></span>
            </div>
            <ul class="epc-cert-list">
              <li v-for="(c, i) in certification.breakdown" :key="'cert-' + i" class="epc-cert-row">
                <div class="epc-cert-row__top">
                  <b>{{ c.certification_number || ('#' + c.certification_id) }}</b>
                  <span class="epc-cert-row__meters">{{ fmt(c.meters) }} m</span>
                </div>
                <div class="epc-cert-row__sub">
                  <span>{{ c.order_code || '—' }}</span>
                  <span> · {{ c.date || '—' }}</span>
                  <span v-if="c.contractor"> · {{ c.contractor }}</span>
                </div>
              </li>
            </ul>
          </template>
          <div v-else class="epc-info-popover__empty">{{ $t('not_certified') }}</div>
        </b-tab-item>

        <!-- Progreso registrado -->
        <b-tab-item>
          <template #header>
            <b-icon icon="history" pack="fas" size="is-small" />
            <span class="ml-1">{{ $t('progress') }}</span>
            <span v-if="totalLength" class="epc-muted ml-1">· {{ fmt(totalLength) }} m</span>
          </template>
          <div v-if="progress.length === 0" class="epc-info-popover__empty">
            {{ $t('no_progress_registered') }}
          </div>
          <ul v-else class="epc-progress-list">
            <li v-for="(p, i) in progress" :key="'prog-' + i" class="epc-progress-row">
              <span class="epc-dot" :style="{ backgroundColor: p.status_color || '#666' }"></span>
              <div class="epc-progress-row__body">
                <div class="epc-progress-row__top">
                  <b>{{ p.status_name || '—' }}</b>
                  <span class="epc-progress-row__meters">{{ fmt(p.meters) }} m</span>
                </div>
                <div class="epc-progress-row__sub">
                  <span>{{ p.created_at || '' }}</span>
                  <b-icon
                    v-if="p.certified"
                    icon="certificate"
                    pack="fas"
                    size="is-small"
                    class="epc-certified-icon"
                    :title="$t('already_certified')"
                  />
                </div>
              </div>
            </li>
          </ul>
        </b-tab-item>
      </b-tabs>
    </div>
  </div>
  </div>
</template>

<script>
export default {
  name: 'ElectricalInfoPopover',

  props: {
    // Posición ancla (click) en coords de viewport.
    position: { type: Object, required: true },
    // Info resuelta por el backend (getElectricalComponentInfo). null mientras carga.
    data:     { type: Object, default: null },
    loading:  { type: Boolean, default: false },
    // Contorno del segmento (vértices proyectados a pantalla) + punto ancla del
    // leader line (centroide del segmento). Los recalcula el padre por rAF.
    segmentPoints: { type: Array,  default: () => [] },
    anchor:        { type: Object, default: null },
  },

  data() {
    return {
      activeTab: 0,
      popoverWidth: 400,
      popoverHeight: 260,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    };
  },

  computed: {
    extras() {
      return (this.data && this.data.extra_attributes) || {};
    },
    certification() {
      return (this.data && this.data.certification) || null;
    },
    progress() {
      return (this.data && this.data.history && this.data.history.progress) || [];
    },
    totalLength() {
      return this.data && this.data.total_length != null ? Number(this.data.total_length) : null;
    },
    title() {
      const h = this.data && this.data.metadata && this.data.metadata.handle;
      return h ? `#${h}` : this.$t('element');
    },

    // Mantiene el popover dentro del viewport (flip horizontal/vertical + clamp).
    // Offset amplio (GAP) para dejar lugar al leader line entre el popover y el
    // segmento.
    adjusted() {
      const margin = 10;
      const GAP = 18;
      let x = this.position.x + GAP;
      let y = this.position.y + GAP;
      if (x + this.popoverWidth > this.windowWidth - margin) {
        x = this.position.x - this.popoverWidth - GAP;
      }
      if (x < margin) x = margin;
      if (y + this.popoverHeight > this.windowHeight - margin) {
        y = this.position.y - this.popoverHeight - GAP;
      }
      if (y < margin) y = margin;
      return { x, y };
    },

    // Puntos del contorno del segmento como string para <polyline>.
    polylineStr() {
      const pts = this.segmentPoints || [];
      if (pts.length < 2) return '';
      return pts.map(p => `${p.x},${p.y}`).join(' ');
    },

    // Inicio del leader line = punto del BORDE del popover más cercano al ancla
    // (clampeamos el ancla dentro del rect del popover → punto de borde más próximo).
    leaderStart() {
      if (!this.anchor) return null;
      const x1 = this.adjusted.x;
      const y1 = this.adjusted.y;
      const x2 = x1 + this.popoverWidth;
      const y2 = y1 + this.popoverHeight;
      return {
        x: Math.max(x1, Math.min(this.anchor.x, x2)),
        y: Math.max(y1, Math.min(this.anchor.y, y2)),
      };
    },
  },

  mounted() {
    // Cerrar al hacer click fuera (con delay para no capturar el click que lo abre).
    setTimeout(() => document.addEventListener('click', this._onClickOutside), 50);
    window.addEventListener('resize', this._onResize);
    this.$nextTick(this._measure);
  },

  updated() {
    this._measure();
  },

  beforeDestroy() {
    document.removeEventListener('click', this._onClickOutside);
    window.removeEventListener('resize', this._onResize);
  },

  methods: {
    fmt(v) {
      const n = Number(v);
      return Number.isFinite(n) ? n.toFixed(2) : '0.00';
    },
    _measure() {
      const el = this.$refs.box;
      if (el && el.offsetHeight > 0) this.popoverHeight = el.offsetHeight;
    },
    _onResize() {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
    },
    _onClickOutside(e) {
      // Solo la CAJA del popover cuenta como "adentro" (el overlay SVG cubre todo
      // el viewport pero no captura clicks ni debe impedir el cierre).
      const el = this.$refs.box;
      if (!el) return;
      let node = e.target;
      while (node) {
        if (node === el) return;
        node = node.parentNode;
      }
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
/* Contenedor a viewport completo: aloja el SVG (contorno + leader) y la caja.
   No captura clicks (solo la caja, vía pointer-events: auto). */
.epc-info-overlay {
  position: fixed;
  inset: 0;
  z-index: 99998;
  pointer-events: none;
}
.epc-info-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
}
/* Contorno del segmento: halo tenue + línea sólida. */
.epc-seg-halo {
  fill: none;
  stroke: #93c5fd;
  stroke-width: 8;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.5;
}
.epc-seg-line {
  fill: none;
  stroke: #2563eb;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}
/* Leader line punteado del popover al segmento + marcador en el ancla. */
.epc-leader {
  stroke: #2563eb;
  stroke-width: 1.5;
  stroke-dasharray: 4 3;
}
.epc-marker {
  fill: #2563eb;
  stroke: #fff;
  stroke-width: 1.5;
}

.epc-info-popover {
  position: fixed;
  width: 400px;
  max-width: calc(100vw - 20px);
  background: #fff;
  border: 2px solid #5577bb;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  z-index: 99999;
  overflow: hidden;
  font-size: 12px;
  pointer-events: auto;
}

.epc-info-popover__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #5577bb;
  color: #fff;
  font-weight: 600;
}
.epc-info-popover__title { font-size: 13px; }
.epc-info-popover__close {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  opacity: 0.85;
  transition: opacity 120ms ease;
}
.epc-info-popover__close:hover { opacity: 1; }

.epc-info-popover__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 12px;
  color: #6b7280;
}

.epc-info-popover__body {
  padding: 6px 8px 8px;
}

/* Tabs compactas; el contenido de cada tab hace scroll si crece. */
.epc-info-popover__tabs >>> .tabs { margin-bottom: 6px; }
.epc-info-popover__tabs >>> .tabs ul { border-bottom-color: #e5e7eb; }
.epc-info-popover__tabs >>> .tab-content { padding: 0; }
.epc-info-popover__tabs >>> .tab-item {
  max-height: 46vh;
  overflow-y: auto;
}
.epc-muted { color: #9ca3af; font-weight: 500; text-transform: none; letter-spacing: 0; }

.epc-kv {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
}
.epc-kv li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 10px;
  border-bottom: 1px solid #f3f4f6;
}
.epc-kv li:last-child { border-bottom: none; }
.epc-kv li:nth-child(odd) { background: #f9fafb; }
.epc-kv span { color: #6b7280; font-weight: 500; flex-shrink: 0; }
.epc-kv b { color: #111827; font-weight: 600; text-align: right; word-break: break-word; }

.epc-cert-total {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  margin-bottom: 6px;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #86efac;
  font-weight: 700;
}

.epc-cert-list, .epc-progress-list { list-style: none; margin: 0; padding: 0; }
.epc-cert-row {
  padding: 6px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 6px;
}
.epc-cert-row:last-child { margin-bottom: 0; }
.epc-cert-row__top { display: flex; justify-content: space-between; align-items: center; }
.epc-cert-row__meters { color: #15803d; font-weight: 700; }
.epc-cert-row__sub { color: #6b7280; font-size: 11px; margin-top: 2px; }

.epc-progress-row { display: flex; align-items: flex-start; gap: 8px; padding: 6px 0; border-bottom: 1px solid #f3f4f6; }
.epc-progress-row:last-child { border-bottom: none; }
.epc-dot { flex-shrink: 0; width: 12px; height: 12px; border-radius: 3px; margin-top: 2px; border: 1px solid rgba(0,0,0,0.15); }
.epc-progress-row__body { flex: 1; min-width: 0; }
.epc-progress-row__top { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.epc-progress-row__meters { color: #374151; font-weight: 600; }
.epc-progress-row__sub { display: flex; align-items: center; gap: 6px; color: #9ca3af; font-size: 11px; margin-top: 1px; }
.epc-certified-icon { color: #15803d; }

.epc-info-popover__empty { color: #9ca3af; font-style: italic; padding: 4px 0; }
</style>
